import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { Button } from '@/components/ui/Button'
import { Container } from '@/components/ui/Container'
import { MobileMenu } from './MobileMenu'
import { useScrollDirection } from '@/hooks/useScrollDirection'
import { cn } from '@/lib/utils'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

export function Header() {
  const { t, i18n } = useTranslation()
  const { scrollY } = useScrollDirection()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  const isScrolled = scrollY > 50

  const navLinks = [
    { key: 'about' as const, label: t('nav.about') },
    { key: 'divisions' as const, label: t('nav.divisions') },
    { key: 'certifications' as const, label: t('nav.certifications') },
    { key: 'careers' as const, label: t('nav.careers') },
    { key: 'contacts' as const, label: t('nav.contacts') },
  ]

  // Controlla se un link è attivo confrontando il pathname corrente
  const isActive = (routeKey: RouteKey) => {
    const currentLang = i18n.language as 'it' | 'en'
    const routePath = localizedRoutes[routeKey][currentLang]
    const currentPath = location.pathname.replace(/^\/(it|en)\//, '')
    return currentPath === routePath || location.pathname.includes(routePath)
  }

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-normal',
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        )}
      >
        <Container>
          <div className="flex h-16 items-center justify-between lg:h-20">
            {/* Logo */}
            <LocalizedLink to="home" className="relative z-10 flex-shrink-0">
              <img
                src="/media/loghi/cropped-logo.bluGT_.news_.png"
                alt="Gruppo Tecnocart"
                className={cn('h-10 w-auto transition-all lg:h-12', !isScrolled && 'brightness-0 invert')}
              />
            </LocalizedLink>

            {/* Desktop Nav */}
            <nav className="hidden items-center gap-1 lg:flex" aria-label={t('nav.main', 'Navigazione principale')}>
              {navLinks.map((link) => {
                const active = isActive(link.key)
                return (
                  <LocalizedLink
                    key={link.key}
                    to={link.key}
                    className={cn(
                      'relative rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isScrolled
                        ? active
                          ? 'text-primary-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        : active
                          ? 'text-white'
                          : 'text-white/90 hover:bg-white/10 hover:text-white'
                    )}
                  >
                    {link.label}
                    {/* Sottolineatura attiva */}
                    <span
                      className={cn(
                        'absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full transition-all duration-300',
                        active
                          ? isScrolled
                            ? 'bg-primary-600'
                            : 'bg-white'
                          : 'w-0 opacity-0'
                      )}
                    />
                  </LocalizedLink>
                )
              })}
            </nav>

            {/* Desktop Right */}
            <div className="hidden items-center gap-4 lg:flex">
              <LanguageSelector variant="desktop" isScrolled={isScrolled} />
              <LocalizedLink to="contacts">
                <Button variant={isScrolled ? 'primary' : 'outlineWhite'} size="sm">
                  {t('nav.cta')}
                </Button>
              </LocalizedLink>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className={cn(
                'relative z-10 rounded-md p-2 lg:hidden',
                isScrolled ? 'text-gray-900' : 'text-white'
              )}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </Container>


      </header>

      {/* Mobile Menu */}
      <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </>
  )
}
